import type { SpecialtyOfferingCard } from '../../api/types.js';
import { color, font, fontSize, radius, seriesColor, space, tint } from '../../theme/tokens.js';
import { formatValue, formatValueExact } from '../MetricCell/format.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { ProvenanceFootnote } from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';

/**
 * The two offering summary cards (FR-S20, blueprint § Specialties).
 *
 * ⚠ **These respond to the region strip and to NOTHING else** — not the offering toggle, not the
 * row selection (FR-S21). They are the fixed frame of reference the rows are compared against,
 * and a frame that moved with the selection would not be one.
 *
 * ⚠ **No disclosure note.** An earlier revision required the Allied card to declare how much of
 * its figure was candidates with no profession recorded — roughly 29% of it. FR-S09 removed that
 * population entirely by reading the ownership record, which is populated for every candidate.
 * A note reading "includes 0 with no profession recorded" would be worse than none.
 */

export interface OfferingCardsProps {
  nursing: SpecialtyOfferingCard;
  allied: SpecialtyOfferingCard;
}

/** `—` for a figure that could not be computed, never `0` (FR-S27, FR-055). */
function fmt(value: number | null): string {
  return formatValue(value);
}

function Card({
  title,
  accent,
  card,
}: {
  title: string;
  accent: string;
  card: SpecialtyOfferingCard;
}) {
  return (
    <section
      aria-label={title}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: radius.lg,
        padding: `${space['5xl']} ${space['6xl']}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space['3xl'] }}>
        <h2
          style={{
            margin: 0,
            fontSize: fontSize.cardTitle,
            fontWeight: 700,
            letterSpacing: '-.3px',
            color: accent,
          }}
        >
          {title}
        </h2>
        <span
          style={{
            fontFamily: font.mono,
            fontSize: '11px',
            fontWeight: 600,
            color: accent,
            background: tint(accent, 10),
            padding: '2px 9px',
            borderRadius: '20px',
          }}
        >
          {card.recruiterPct == null ? '—' : `${card.recruiterPct}% of recruiters`}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: space['3xl'],
          marginTop: space['4xl'],
        }}
      >
        <Figure label="Recruiters" metricKey="recruiters" value={card.recruiters} />
        <Figure label="Book of Business" metricKey="bob" value={card.bob} />
        <Figure
          label="Active TOA"
          metricKey="activeToa"
          value={card.activeToa}
          accent={seriesColor.toa}
        />
        <Figure label="Pending" metricKey="pending" value={card.pending} accent={color.warning} />
      </div>
    </section>
  );
}

/**
 * One of the four figures on a card.
 *
 * ⚠ **The disclosure is new on 2026-09-10 and is the only thing that has ever hidden behind
 * these numbers.** The note at the top of this file records why there is no *data-quality*
 * disclosure here and that is unchanged — this is lineage, not a caveat about the figure's
 * completeness. These are the largest numerals on the Specialties page and were, until now, the
 * only prominent figures in the product a reader could not ask a question of.
 */
function Figure({
  label,
  metricKey,
  value,
  accent,
}: {
  label: string;
  metricKey: string;
  value: number | null;
  accent?: string;
}) {
  return (
    <div>
      <Disclosure
        accessibleName={`${label}: ${formatValueExact(value)}`}
        panel={
          <span style={{ display: 'block' }}>
            <strong>{label}</strong>
            <span style={{ display: 'block', fontFamily: font.mono }}>
              {formatValueExact(value)}
            </span>
            <ProvenanceFootnote provenance={provenanceFor(metricKey)} />
          </span>
        }
        style={{ padding: 0, background: 'none', border: 'none', cursor: 'help' }}
      >
        <span
          style={{
            fontFamily: font.mono,
            fontSize: '26px',
            fontWeight: 600,
            color: accent ?? color.ink,
          }}
        >
          {fmt(value)}
        </span>
      </Disclosure>
      <div style={{ fontSize: '11.5px', color: color.inkMuted }}>{label}</div>
    </div>
  );
}

export function OfferingCards({ nursing, allied }: OfferingCardsProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: space['4xl'],
        marginBottom: space['4xl'],
      }}
    >
      <Card title="Nursing" accent={color.nursing} card={nursing} />
      <Card title="Allied" accent={color.allied} card={allied} />
    </div>
  );
}
